import { Typography } from '@mui/material';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GenericSearchControls from '../components/common/GenericSearchControls';
import PackagesByAddressSummary, {
  UNMATCHED_ID
} from '../components/package_manager/PackagesByAddressSummary';
import JqlSearchBar from '../components/package_manager/JqlSearchBar';
import PackageTable from '../components/package_manager/PackageTable';
import PaginatedControls from '../components/package_manager/PaginatedControls';
import SearchQuerySummary from '../components/package_manager/SearchQuerySummary';
import packageManagerService from '../services/package-manager-service';
import { storageHandler } from '../utils/storage_handler';

// Superadmin usertype, per App.js's PrivateRoute usertype gating.
const SUPERADMIN_USERTYPE = 1;

export default function PackageManagerPage() {
  const [packages, setPackages] = useState([]);
  // ─── Search mode ──────────────────────────────────────────────────────────
  const [searchMode, setSearchMode] = useState('basic'); // 'basic' | 'jql'
  // ─── JQL query state ───────────────────────────────────────────────────────
  const [jqlQuery, setJqlQuery] = useState('');
  const [jqlError, setJqlError] = useState('');
  // ─── Basic search state ────────────────────────────────────────────────────
  const [basicQuery, setBasicQuery] = useState('');
  // ──────────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20
  });
  const [addressSummary, setAddressSummary] = useState([]);
  const [addressSummaryLoading, setAddressSummaryLoading] = useState(false);
  const [addressSummaryMeta, setAddressSummaryMeta] = useState({});
  const [addressSummaryPagination, setAddressSummaryPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 12,
    total: 0
  });
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSortBy, setAddressSortBy] = useState('count');
  const [addressSortOrder, setAddressSortOrder] = useState('DESC');

  // Selected client addresses. Multiple selections OR together in the search.
  const [selectedAddressIds, setSelectedAddressIds] = useState([]);
  const [includeUnmatched, setIncludeUnmatched] = useState(false);
  // Labels are memoised at selection time so a tile selected on page 1 still
  // shows its name in the badge while you are looking at page 3.
  const [selectedAddressLabels, setSelectedAddressLabels] = useState({});

  // Guards against an older summary response overwriting a newer one — both
  // from StrictMode's double mount effect and from fast typing in tile search.
  const summaryRequestId = useRef(0);

  // The address matcher recomputes on every package/address write already —
  // this is the manual "force it now" escape hatch (threshold tuning, a bulk
  // vendor import, ops correcting drift), so it's gated to superadmins the
  // same way the backend route is.
  const [recounting, setRecounting] = useState(false);
  const canRecount =
    parseInt(storageHandler.localStorageGet('usertype'), 10) ===
    SUPERADMIN_USERTYPE;

  useEffect(() => {
    const savedFilters = localStorage.getItem('advancedSearchFilters');
    if (savedFilters) setFilters(JSON.parse(savedFilters));
  }, []);

  const fetchAddressSummary = useCallback(
    async (overrides = {}) => {
      const requestId = ++summaryRequestId.current;
      setAddressSummaryLoading(true);
      try {
        const response = await packageManagerService.getPackagesByAddressSummary({
          page: overrides.page ?? addressSummaryPagination.currentPage,
          limit: addressSummaryPagination.limit,
          search: overrides.search ?? addressSearch,
          sortBy: overrides.sortBy ?? addressSortBy,
          sortOrder: overrides.sortOrder ?? addressSortOrder
        });

        // A newer request has already landed — drop this stale response.
        if (requestId !== summaryRequestId.current) return;

        if (response.success) {
          setAddressSummary(response.data);
          setAddressSummaryMeta(response.summary ?? {});
          setAddressSummaryPagination((prev) => ({
            ...prev,
            ...response.pagination,
            total: response.total
          }));
        }
      } catch (error) {
        console.error('Failed to fetch address summary:', error);
      } finally {
        if (requestId === summaryRequestId.current) {
          setAddressSummaryLoading(false);
        }
      }
    },
    [
      addressSummaryPagination.currentPage,
      addressSummaryPagination.limit,
      addressSearch,
      addressSortBy,
      addressSortOrder
    ]
  );

  // Forces a full re-run of the address matcher across every package, then
  // refreshes the displayed counts. Returns the stats so the panel can show
  // them, and throws on failure so the panel's own try/catch can show an
  // error — this component intentionally has no other error-reporting path.
  const handleRecountAll = useCallback(async () => {
    setRecounting(true);
    try {
      const response = await packageManagerService.recomputeAddressMatches();
      if (!response.success) {
        throw new Error(response.reason || 'Recount failed');
      }
      await fetchAddressSummary();
      return response.data;
    } finally {
      setRecounting(false);
    }
  }, [fetchAddressSummary]);

  // Selecting a tile no longer rewrites the JQL box. The ids go to the backend
  // as `address_ids`, which filters on the same matched_address_id column the
  // tile counts come from — so the table total always equals the tile count.
  const handleToggleAddress = useCallback((row) => {
    setSelectedAddressIds((prev) =>
      prev.includes(row.address_id)
        ? prev.filter((id) => id !== row.address_id)
        : [...prev, row.address_id]
    );
    setSelectedAddressLabels((prev) =>
      prev[row.address_id]
        ? prev
        : { ...prev, [row.address_id]: row.label }
    );
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleToggleUnmatched = useCallback(() => {
    setIncludeUnmatched((prev) => !prev);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleRemoveSelection = useCallback((id) => {
    if (id === UNMATCHED_ID) {
      setIncludeUnmatched(false);
    } else {
      setSelectedAddressIds((prev) => prev.filter((x) => x !== id));
    }
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleClearSelections = useCallback(() => {
    setSelectedAddressIds([]);
    setIncludeUnmatched(false);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleAddressSearchChange = useCallback((value) => {
    setAddressSearch(value);
    setAddressSummaryPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleAddressSortChange = useCallback((field, order) => {
    setAddressSortBy(field);
    setAddressSortOrder(order);
    setAddressSummaryPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const handleAddressPageChange = useCallback((page) => {
    setAddressSummaryPagination((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const fetchAllPackages = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      try {
        const response = await packageManagerService.getPackageDetailsList(
          page,
          limit
        );
        if (response.success) {
          setPackages(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPackages([]);
          console.error(response.message);
        }
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      }
      setLoading(false);
    },
    [pagination.limit]
  );

  // Refetches on mount and whenever the tile query (search / sort / page)
  // changes, since those are fetchAddressSummary's own dependencies.
  useEffect(() => {
    fetchAddressSummary();
  }, [fetchAddressSummary]);

  useEffect(() => {
    localStorage.setItem('advancedSearchFilters', JSON.stringify(filters));
  }, [filters]);

  const handleSearch = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      setJqlError('');
      try {
        const response =
          await packageManagerService.fetchAndProcessPackageSearch({
            jqlQuery: searchMode === 'jql' ? jqlQuery : '',
            basicQuery: searchMode === 'basic' ? basicQuery : '',
            filters,
            dateFilter,
            dateRangeStart,
            dateRangeEnd,
            sortBy,
            sortOrder,
            page,
            limit,
            addressIds: selectedAddressIds,
            includeUnmatched
          });

        if (response.success) {
          setPackages(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setPackages([]);
          // Surface JQL syntax errors to the search bar
          if (response.message && response.message.startsWith('JQL syntax error')) {
            setJqlError(response.message);
          } else {
            console.error(response.message);
          }
        }
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      }
      setLoading(false);
      // Keep the tile counts current: the mobile app writes packages while a
      // web user is looking at this page.
      fetchAddressSummary();
    },
    [
      jqlQuery,
      basicQuery,
      searchMode,
      filters,
      dateFilter,
      dateRangeStart,
      dateRangeEnd,
      sortBy,
      sortOrder,
      pagination.limit,
      selectedAddressIds,
      includeUnmatched,
      fetchAddressSummary
    ]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((page, limit) => {
        handleSearch(page, limit);
      }, 500),
    [handleSearch]
  );

  // handleSearch (and so debouncedSearch) is recreated on every relevant
  // state change; cancel the outgoing one so an unmounted/replaced instance
  // never fires a stale search later.
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const hasSearchCriteria =
    (searchMode === 'basic' ? basicQuery.trim() : jqlQuery.trim()) ||
    filters.length > 0 ||
    dateFilter !== 'default' ||
    dateRangeStart || dateRangeEnd ||
    sortBy !== 'default' ||
    sortOrder !== 'default' ||
    selectedAddressIds.length > 0 ||
    includeUnmatched;

  // Drives the packages table on mount and on every search-criteria change —
  // including selecting/deselecting an address chip. When criteria are absent
  // (e.g. the last chip was just removed) this MUST still refresh: falling
  // through silently here was the bug where clearing the last filter left the
  // stale filtered table on screen forever, since nothing else re-fetches.
  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    if (hasSearchCriteria) {
      debouncedSearch(1, pagination.limit);
    } else {
      // Cancel any in-flight debounced search so a slower stale response
      // can't land after this and re-apply a filter that was just cleared.
      debouncedSearch.cancel();
      fetchAllPackages(1, pagination.limit);
    }
  }, [
    jqlQuery,
    basicQuery,
    searchMode,
    filters,
    dateFilter,
    dateRangeStart,
    dateRangeEnd,
    sortBy,
    sortOrder,
    pagination.limit,
    selectedAddressIds,
    includeUnmatched,
    debouncedSearch,
    hasSearchCriteria,
    fetchAllPackages,
  ]);

  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  const handleColumnSort = useCallback(
    (field, order) => {
      setSortBy(field);
      setSortOrder(order);
    },
    [setSortBy, setSortOrder]
  );

  const resetAll = useCallback(() => {
    setJqlQuery('');
    setJqlError('');
    setBasicQuery('');
    setFilters([]);
    setDateFilter('default');
    setDateRangeStart('');
    setDateRangeEnd('');
    setSortBy('default');
    setSortOrder('default');
    setPagination({ currentPage: 1, totalPages: 1, limit: 20 });
    setShowAdvancedSearch(false);
    setFilterDraft({ field: '', value: '' });
    setSelectedAddressIds([]);
    setIncludeUnmatched(false);
    setSelectedAddressLabels({});
    setAddressSearch('');
    setAddressSortBy('count');
    setAddressSortOrder('DESC');
    setAddressSummaryPagination((prev) => ({ ...prev, currentPage: 1 }));
    localStorage.removeItem('advancedSearchFilters');
    fetchAllPackages();
    fetchAddressSummary();
  }, [fetchAllPackages, fetchAddressSummary]);

  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      setFilters((prev) => [...prev, filterDraft]);
      setFilterDraft({ field: '', value: '' });
    }
  };

  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  const formatFieldLabel = (field) => {
    switch (field) {
      case 'direction_type':
        return 'Direction Type';
      case 'recipient_name':
        return 'Recipient Name';
      case 'barcode_list':
        return 'Barcode';
      case 'order_number':
        return 'Order Number';
      case 'uploaded_by':
        return 'Uploaded By';
      case 'address_list':
        return 'Address';
      default:
        return field;
    }
  };

  const filterOptions = [
    { value: 'direction_type', label: 'Direction Type' },
    { value: 'barcode_list', label: 'Barcode' },
    { value: 'order_number', label: 'Order Number' },
    { value: 'uploaded_by', label: 'Uploaded By' },
  ];

  const sortByList = [
    { value: 'id', label: 'ID' },
    { value: 'direction_type', label: 'Direction Type' },
    { value: 'company_name', label: 'Company' },
    { value: 'tracking_number', label: 'Tracking Number' },
    { value: 'uploaded_by', label: 'Uploaded By' },
    { value: 'createdAt', label: 'Created At' }
  ];

  return (
    <div>
      {/* Add spacing so content isn't hidden behind AppBar */}
      <div style={{ marginTop: 40, padding: 20 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Package Manager Page
        </Typography>
        <div style={{ margin: '20px auto', width: '80%' }}>

          {/* Search bar — Basic and JQL modes */}
          <JqlSearchBar
            mode={searchMode}
            onModeChange={setSearchMode}
            value={jqlQuery}
            onChange={setJqlQuery}
            basicValue={basicQuery}
            onBasicChange={setBasicQuery}
            onSearch={handleSearchClick}
            onReset={resetAll}
            error={jqlError}
          />

          {/* Sort / date / advanced-filter controls (search bar hidden) */}
          <GenericSearchControls
            searchValue=""
            setSearchValue={() => {}}
            hideSearchBar={true}
            sortByList={sortByList}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            enableDateFilter={true}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            enableDateRangePicker={true}
            dateRangeStart={dateRangeStart}
            setDateRangeStart={setDateRangeStart}
            dateRangeEnd={dateRangeEnd}
            setDateRangeEnd={setDateRangeEnd}
            filters={filters}
            setFilters={setFilters}
            filterDraft={filterDraft}
            setFilterDraft={setFilterDraft}
            showAdvancedSearch={showAdvancedSearch}
            setShowAdvancedSearch={setShowAdvancedSearch}
            handleSearchClick={handleSearchClick}
            filterOptions={filterOptions}
            addFilter={addFilter}
            removeFilter={removeFilter}
            formatFieldLabel={formatFieldLabel}
            conditionalFieldInputs={[
              { field: 'direction_type', label: 'Direction Type', options: [{ value: 'incoming', label: 'Incoming' }, { value: 'outgoing', label: 'Outgoing' }] },
              { field: 'has_tracking', label: 'Has Tracking', options: [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }] }
            ]}
            resetAll={resetAll}
          />

          <PackagesByAddressSummary
            rows={addressSummary}
            summary={addressSummaryMeta}
            pagination={addressSummaryPagination}
            loading={addressSummaryLoading}
            search={addressSearch}
            sortBy={addressSortBy}
            sortOrder={addressSortOrder}
            selectedIds={selectedAddressIds}
            selectedLabels={selectedAddressLabels}
            includeUnmatched={includeUnmatched}
            onSearchChange={handleAddressSearchChange}
            onSortChange={handleAddressSortChange}
            onPageChange={handleAddressPageChange}
            onToggleAddress={handleToggleAddress}
            onToggleUnmatched={handleToggleUnmatched}
            onRemoveSelection={handleRemoveSelection}
            onClearSelections={handleClearSelections}
            onRefresh={fetchAddressSummary}
            canRecount={canRecount}
            recounting={recounting}
            onRecountAll={handleRecountAll}
          />

          {(basicQuery.trim() || jqlQuery.trim() || filters.length > 0 || dateFilter !== 'default' || dateRangeStart || dateRangeEnd || selectedAddressIds.length > 0 || includeUnmatched) && (
            <SearchQuerySummary
              searchMode={searchMode}
              basicQuery={basicQuery}
              jqlQuery={jqlQuery}
              filters={filters}
              dateFilter={dateFilter}
              formatFieldLabel={formatFieldLabel}
              addressLabels={[
                ...selectedAddressIds.map(
                  (id) => selectedAddressLabels[id] ?? `#${id}`
                ),
                ...(includeUnmatched ? ['Unmatched'] : [])
              ]}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={packages.length}
            searchValue={jqlQuery}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllPackages}
            isSearchActive={!!hasSearchCriteria}
          />

          {loading ? <p>Loading...</p> : (
            <PackageTable
              packages={packages}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleColumnSort}
            />
          )}

          <PaginatedControls
            pagination={pagination}
            setPagination={setPagination}
            packagesLength={packages.length}
            searchValue={jqlQuery}
            filters={filters}
            handleSearch={handleSearch}
            fetchAll={fetchAllPackages}
            isSearchActive={!!hasSearchCriteria}
          />
        </div>
      </div>
    </div>
  );
}
